import React, { useState, useEffect } from 'react';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { doc, getDoc, collection, query, orderBy, onSnapshot, Timestamp, collectionGroup, updateDoc } from 'firebase/firestore';
import { UserProfile, UserResult, Question, Feedback, Category } from '../types';
import { Markdown } from './Markdown';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell 
} from 'recharts';
import { Users, ClipboardList, TrendingUp, TrendingDown, Minus, Calendar, ArrowLeft, Loader2, Search, User as UserIcon, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Report } from './Report';

export const MetricsPage: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [allResults, setAllResults] = useState<UserResult[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [appConfig, setAppConfig] = useState<any>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const activeSelectedUser = selectedUser ? (users.find(u => u.uid === selectedUser.uid) || selectedUser) : null;

  const handleTogglePaying = async (userId: string, currentStatus: boolean) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isPaying: !currentStatus });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${userId}`);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // Use onSnapshot to be reactive to role changes (e.g. from App.tsx sync)
        const profileUnsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
          if (snap.exists()) {
            const profile = snap.data() as UserProfile;
            setUserProfile(profile);
            setIsAdmin(profile.role === 'admin');
          } else {
            setIsAdmin(false);
          }
          setLoading(false);
        }, (error) => {
          console.error("Error listening to user profile:", error);
          setLoading(false);
        });
        return () => profileUnsub();
      } else {
        setIsAdmin(false);
        setUserProfile(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    const usersUnsub = onSnapshot(query(collection(db, 'users'), orderBy('createdAt', 'desc')), (snap) => {
      setUsers(snap.docs.map(d => ({ ...d.data(), uid: d.id } as UserProfile)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const resultsUnsub = onSnapshot(collectionGroup(db, 'results'), (snap) => {
      setAllResults(snap.docs.map(d => ({ ...d.data(), id: d.id } as UserResult)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'all_results');
    });

    const questionsUnsub = onSnapshot(query(collection(db, 'questions'), orderBy('order')), (snap) => {
      setQuestions(snap.docs.map(d => ({ ...d.data(), id: d.id } as Question)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'questions');
    });

    const feedbackUnsub = onSnapshot(collection(db, 'feedback'), (snap) => {
      setFeedback(snap.docs.map(d => ({ ...d.data(), id: d.id } as Feedback)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'feedback');
    });

    const configUnsub = onSnapshot(doc(db, 'config', 'app'), (snap) => {
      if (snap.exists()) {
        setAppConfig(snap.data());
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/app');
    });
    
    return () => {
      usersUnsub();
      resultsUnsub();
      questionsUnsub();
      feedbackUnsub();
      configUnsub();
    };
  }, [isAdmin]);

  useEffect(() => {
    setSelectedResultId(null);
  }, [selectedUser]);

  useEffect(() => {
    if (!isAdmin || !selectedUser) {
      setUserResults([]);
      return;
    }

    setResultsLoading(true);
    const resultsUnsub = onSnapshot(query(collection(db, 'users', selectedUser.uid, 'results'), orderBy('timestamp', 'desc')), (snap) => {
      setUserResults(snap.docs.map(d => ({ ...d.data(), id: d.id } as UserResult)));
      setResultsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, `users/${selectedUser.uid}/results`);
      setResultsLoading(false);
    });

    return () => resultsUnsub();
  }, [isAdmin, selectedUser]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-bg-main flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h1>
        <p className="text-text-secondary mb-8">You do not have permission to view this page.</p>
        <Link to="/" className="px-6 py-2 bg-bg-card border border-border-secondary rounded-lg text-text-secondary hover:bg-bg-main transition-all uppercase tracking-widest text-xs">
          Return to Dashboard
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center text-text-muted">
        <Loader2 className="animate-spin mr-3" />
        <span className="uppercase tracking-widest text-xs">Loading Metrics...</span>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (u.displayName && u.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Overall System Stats
  const totalUsers = users.length;
  const totalTests = allResults.length;
  
  const systemAverageScores = allResults.reduce((acc, res) => {
    acc.responsibility += res.scores.responsibility;
    acc.selftrust += res.scores.selftrust;
    acc.care += res.scores.care;
    return acc;
  }, { responsibility: 0, selftrust: 0, care: 0 });

  const systemCount = allResults.length || 1;
  const systemAvgData = [
    { name: 'Responsibility', value: Number((systemAverageScores.responsibility / systemCount).toFixed(2)) },
    { name: 'Self Trust', value: Number((systemAverageScores.selftrust / systemCount).toFixed(2)) },
    { name: 'Care', value: Number((systemAverageScores.care / systemCount).toFixed(2)) },
  ];

  // Stats for the selected user if any, otherwise aggregate (if we had them)
  const displayResults = selectedUser ? userResults : [];
  
  const latestResult = userResults[0];
  const previousResult = userResults[1];
  const activeResult = userResults.find(r => r.id === selectedResultId) || userResults[0];

  const activeIndex = selectedResultId 
    ? userResults.findIndex(r => r.id === selectedResultId) 
    : 0;

  const resultsToPass = activeIndex !== -1 
    ? userResults.slice(activeIndex) 
    : userResults;

  const scoresToPass = activeResult ? activeResult.scores : { responsibility: 0, selftrust: 0, care: 0 };

  const handleDownloadPDF = async () => {
    if (!selectedUser || !activeResult) return;
    setIsGeneratingPDF(true);

    const sheetsToRestore: { sheet: CSSStyleSheet; wasDisabled: boolean }[] = [];
    const safeStyles: HTMLStyleElement[] = [];

    try {
      const container = document.getElementById('metrics-report-container');
      if (!container) {
        console.error("metrics-report-container element not found");
        return;
      }

      // Convert tailwind v4 oklch rules on the fly to prevent html2canvas color parsing crashes
      try {
        const originalStyleSheets = Array.from(document.styleSheets) as CSSStyleSheet[];
        for (const sheet of originalStyleSheets) {
          try {
            if (sheet.disabled) continue;
            const rules = Array.from(sheet.cssRules);
            let hasOklch = false;
            let cssText = '';
            for (const rule of rules) {
              const text = rule.cssText;
              if (text.includes('oklch')) {
                hasOklch = true;
              }
              cssText += text + '\n';
            }

            if (hasOklch) {
              // Convert oklch(L C H) or oklch(L C H / A) to equivalent rgb or rgba sRGB colors on the fly
              const safeCss = cssText.replace(/oklch\(([^()]*|\([^()]*\))*\)/gi, (match) => {
                try {
                  const inner = match.substring(6, match.length - 1).trim();
                  const parts = inner.split('/');
                  const colorPartsStr = parts[0].trim();
                  const alphaStr = parts[1] ? parts[1].trim() : null;
                  const colorParts = colorPartsStr.split(/\s+/);
                  
                  if (colorParts.length < 3) {
                    return 'rgb(120, 120, 120)';
                  }
                  
                  const lVal = colorParts[0];
                  const cVal = colorParts[1];
                  const hVal = colorParts[2];
                  
                  let l = 0;
                  if (lVal !== 'none') {
                    l = parseFloat(lVal);
                    if (lVal.includes('%')) {
                      l = l / 100;
                    }
                  }
                  
                  let c = 0;
                  if (cVal !== 'none') {
                    c = parseFloat(cVal);
                  }
                  
                  let h = 0;
                  if (hVal !== 'none') {
                    let hDeg = parseFloat(hVal);
                    if (hVal.includes('rad')) {
                      hDeg = hDeg * (180 / Math.PI);
                    } else if (hVal.includes('grad')) {
                      hDeg = hDeg * 0.9;
                    } else if (hVal.includes('turn')) {
                      hDeg = hDeg * 360;
                    }
                    h = hDeg;
                  }
                  
                  // OKLCH to sRGB conversion
                  const hRad = (h * Math.PI) / 180;
                  const oklab_L = l;
                  const oklab_a = c * Math.cos(hRad);
                  const oklab_b = c * Math.sin(hRad);
                  
                  const l_ = oklab_L + 0.3963377774 * oklab_a + 0.2158037573 * oklab_b;
                  const m_ = oklab_L - 0.1055613458 * oklab_a - 0.0638541728 * oklab_b;
                  const s_ = oklab_L - 0.0894841775 * oklab_a - 1.2914855414 * oklab_b;
                  
                  const l_lin = l_ * l_ * l_;
                  const m_lin = m_ * m_ * m_;
                  const s_lin = s_ * s_ * s_;
                  
                  const r_lin = +4.0767416621 * l_lin - 3.3077115913 * m_lin + 0.2309699292 * s_lin;
                  const g_lin = -1.2684380046 * l_lin + 2.6097574011 * m_lin - 0.3413193965 * s_lin;
                  const b_lin = -0.0041960863 * l_lin - 0.7034186147 * m_lin + 1.7076147010 * s_lin;
                  
                  const transfer = (cLin: number) => {
                    if (cLin <= 0.0031308) {
                      return 12.92 * cLin;
                    }
                    return 1.055 * Math.pow(cLin, 1 / 2.4) - 0.055;
                  };
                  
                  const r = Math.round(Math.max(0, Math.min(1, transfer(r_lin))) * 255);
                  const g = Math.round(Math.max(0, Math.min(1, transfer(g_lin))) * 255);
                  const b = Math.round(Math.max(0, Math.min(1, transfer(b_lin))) * 255);
                  
                  if (alphaStr) {
                    if (alphaStr.includes('var(') || alphaStr.includes('calc(')) {
                      return `rgba(${r}, ${g}, ${b}, 0.95)`;
                    }
                    let alpha = parseFloat(alphaStr);
                    if (alphaStr.includes('%')) {
                      alpha = alpha / 100;
                    }
                    if (isNaN(alpha)) alpha = 1;
                    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                  }
                  
                  return `rgb(${r}, ${g}, ${b})`;
                } catch (e) {
                  console.warn("Could not parse oklch color:", match, e);
                  return 'rgb(120, 120, 120)';
                }
              });

              const styleEl = document.createElement('style');
              styleEl.textContent = safeCss;
              styleEl.setAttribute('data-html2canvas-safe', 'true');
              document.head.appendChild(styleEl);
              safeStyles.push(styleEl);

              sheetsToRestore.push({ sheet, wasDisabled: sheet.disabled });
              sheet.disabled = true;
            }
          } catch (e) {
            console.warn("Could not preprocess style sheet:", e);
          }
        }
      } catch (styleErr) {
        console.error("Style conversion error:", styleErr);
      }

      // Temporarily show container for capture
      container.style.display = 'block';
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.visibility = 'visible';
      container.style.zIndex = '9999';

      // Give it a moment to ensure all styles and images are ready
      await new Promise(resolve => setTimeout(resolve, 500));

      const headerElement = container.querySelector('#pdf-header') || document.getElementById('pdf-header');
      const sections = Array.from(container.querySelectorAll('.pdf-section')).filter(el => !el.classList.contains('no-pdf'));

      if (!headerElement) throw new Error("Header element not found");

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12; // mm
      const footerHeight = 15; // mm
      const contentWidth = pdfWidth - (2 * margin);
      
      // Capture Header
      const headerCanvas = await html2canvas(headerElement as HTMLElement, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        windowWidth: 800
      });
      const headerImgData = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerImgHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;

      let currentY = margin;

      const addHeaderAndFooter = (pagePdf: jsPDF) => {
        const year = new Date().getFullYear();
        // Header
        pagePdf.addImage(headerImgData, 'JPEG', margin, margin, contentWidth, headerImgHeight);
        
        // Footer
        pagePdf.setFillColor(255, 255, 255);
        pagePdf.rect(0, pdfHeight - footerHeight, pdfWidth, footerHeight, 'F');
        pagePdf.setFontSize(8);
        pagePdf.setTextColor(150, 150, 150);
        pagePdf.text(`© ${year} Sheila Delaney. All rights reserved.`, pdfWidth / 2, pdfHeight - 7, { align: 'center' });
      };

      addHeaderAndFooter(pdf);
      currentY += headerImgHeight + 10;

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i] as HTMLElement;
        const isInsight = section.classList.contains('pdf-insight-section');
        
        const sectionCanvas = await html2canvas(section, { 
          scale: 2, 
          useCORS: true, 
          backgroundColor: '#ffffff',
          windowWidth: 800
        });
        const sectionImgData = sectionCanvas.toDataURL('image/jpeg', 0.95);
        const sectionImgHeight = (sectionCanvas.height * contentWidth) / sectionCanvas.width;

        // Check if we need a new page
        // Force new page for insights, or if content doesn't fit
        const needsNewPage = isInsight || (currentY + sectionImgHeight > pdfHeight - footerHeight - margin);

        if (needsNewPage && i > 0) {
          pdf.addPage();
          addHeaderAndFooter(pdf);
          currentY = margin + headerImgHeight + 10;
        }

        pdf.addImage(sectionImgData, 'JPEG', margin, currentY, contentWidth, sectionImgHeight);
        currentY += sectionImgHeight + 8;
      }

      const formattedName = selectedUser.displayName?.replace(/\s+/g, '-') || 'User';
      const formattedDate = activeResult.timestamp.toDate().toISOString().split('T')[0];
      pdf.save(`Agency-Report-${formattedName}-${formattedDate}.pdf`);
      
      // Hide container again
      container.style.display = 'none';
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating your PDF. Please try again.");
    } finally {
      // Re-enable and clean up style tags
      for (const { sheet, wasDisabled } of sheetsToRestore) {
        try {
          sheet.disabled = wasDisabled;
        } catch (e) {
          console.warn("Could not restore stylesheet disabled state:", e);
        }
      }
      for (const styleEl of safeStyles) {
        try {
          styleEl.remove();
        } catch (e) {
          console.warn("Could not remove safe style tag:", e);
        }
      }
      setIsGeneratingPDF(false);
    }
  };

  const getTrend = (category: keyof UserResult['scores']) => {
    if (!latestResult || !previousResult) return 'stable';
    const current = latestResult.scores[category];
    const prev = previousResult.scores[category];
    if (current > prev) return 'up';
    if (current < prev) return 'down';
    return 'stable';
  };

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === 'up') return <TrendingUp size={14} className="text-emerald-500" />;
    if (trend === 'down') return <TrendingDown size={14} className="text-red-500" />;
    return <Minus size={14} className="text-text-muted" />;
  };

  const latestData = [
    { name: 'Responsibility', value: latestResult?.scores.responsibility || 0 },
    { name: 'Self Trust', value: latestResult?.scores.selftrust || 0 },
    { name: 'Care', value: latestResult?.scores.care || 0 },
    { name: 'Overall', value: latestResult ? (latestResult.scores.responsibility + latestResult.scores.selftrust + latestResult.scores.care) / 3 : 0 },
  ];

  const COLORS = ['var(--cat-responsibility)', 'var(--cat-selftrust)', 'var(--cat-care)', 'var(--text-main)'];

  const handleDownloadCSV = () => {
    if (users.length === 0) return;

    // Get the latest result for each user
    const userLatestResults = users.map(user => {
      const results = allResults
        .filter(r => r.userId === user.uid)
        .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
      
      return {
        user,
        latestResult: results[0] || null
      };
    });

    // CSV Header
    const headers = ['First Name', 'Last Name', 'Email', 'Responsibility', 'Self Trust', 'Care', 'Overall', 'Date Taken'];
    
    // CSV Rows
    const rows = userLatestResults.map(({ user, latestResult }) => {
      const nameParts = (user.displayName || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const scores = latestResult?.scores || { responsibility: 0, selftrust: 0, care: 0 };
      const overall = (scores.responsibility + scores.selftrust + scores.care).toFixed(1);
      const dateTaken = latestResult?.timestamp ? latestResult.timestamp.toDate().toLocaleString() : 'N/A';

      return [
        `"${firstName}"`,
        `"${lastName}"`,
        `"${user.email}"`,
        scores.responsibility.toFixed(1),
        scores.selftrust.toFixed(1),
        scores.care.toFixed(1),
        overall,
        `"${dateTaken}"`
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Agency-Users-Data-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-bg-main text-text-main p-8 md:p-12">
      <header className="max-w-7xl mx-auto flex justify-between items-center mb-12">
        <div className="flex items-center gap-6">
          <Link to="/" className="p-2 bg-bg-card border border-border-secondary rounded-full text-text-muted hover:text-text-main transition-all">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-[0.75rem] font-light tracking-[0.35em] uppercase text-text-header">ADMIN METRICS</h1>
            <p className="text-[0.6rem] text-text-muted tracking-widest uppercase mt-1">Platform Performance & User Engagement</p>
          </div>
        </div>
        
        <button 
          onClick={handleDownloadCSV}
          className="flex items-center gap-2 px-6 py-2.5 bg-bg-card border border-border-secondary rounded-xl text-text-secondary hover:bg-bg-main hover:text-text-main transition-all uppercase tracking-widest text-[0.65rem] font-bold"
        >
          <ClipboardList size={14} />
          Download Users CSV
        </button>
      </header>

      <main className="max-w-7xl mx-auto space-y-12">
        {/* System Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[0.6rem] text-text-muted font-bold tracking-widest uppercase">Total Users</p>
              <Users size={16} className="text-cat-selftrust" />
            </div>
            <h3 className="text-3xl font-extralight text-text-main">{totalUsers}</h3>
          </div>
          <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[0.6rem] text-text-muted font-bold tracking-widest uppercase">Total Assessments</p>
              <ClipboardList size={16} className="text-cat-responsibility" />
            </div>
            <h3 className="text-3xl font-extralight text-text-main">{totalTests}</h3>
          </div>
          <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[0.6rem] text-text-muted font-bold tracking-widest uppercase">Avg. Platform Score</p>
              <TrendingUp size={16} className="text-cat-care" />
            </div>
            <h3 className="text-3xl font-extralight text-text-main">
              {((systemAverageScores.responsibility + systemAverageScores.selftrust + systemAverageScores.care) / (systemCount * 3)).toFixed(1)}
            </h3>
          </div>
          <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[0.6rem] text-text-muted font-bold tracking-widest uppercase">Tests per User</p>
              <Calendar size={16} className="text-cat-overall" />
            </div>
            <h3 className="text-3xl font-extralight text-text-main">
              {(totalTests / (totalUsers || 1)).toFixed(1)}
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Search & Selection */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-bg-card border border-border-main rounded-3xl p-6 space-y-6">
              <div className="space-y-2">
                <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold">Search Users</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
                  <input 
                    type="text" 
                    placeholder="Name or email..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-bg-input border border-border-secondary rounded-xl py-2.5 pl-10 pr-4 text-sm text-text-main focus:outline-none focus:border-border-focus transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {filteredUsers.map((u) => (
                  <button 
                    key={u.uid} 
                    onClick={() => setSelectedUser(u)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                      selectedUser?.uid === u.uid 
                        ? "bg-bg-main border-border-focus shadow-lg" 
                        : "bg-bg-input border-border-secondary hover:border-border-main"
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-[0.6rem] border",
                      selectedUser?.uid === u.uid ? "bg-bg-card text-text-main border-border-main" : "bg-bg-main text-text-muted border-border-secondary"
                    )}>
                      {u.displayName ? u.displayName.charAt(0) : u.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[0.75rem] text-text-main font-medium truncate">{u.displayName || 'Anonymous User'}</p>
                      <p className="text-[0.6rem] text-text-muted truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-center text-text-muted py-8 text-xs uppercase tracking-widest">No users found</p>
                )}
              </div>
            </div>
          </div>

          {/* User Details & Stats */}
          <div className="lg:col-span-2 space-y-8">
            <AnimatePresence mode="wait">
              {activeSelectedUser ? (
                <motion.div 
                  key={activeSelectedUser.uid}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-8"
                >
                  {/* Selected User Header */}
                  <div className="bg-bg-card border border-border-main rounded-3xl p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-bg-card to-bg-main border border-border-secondary flex items-center justify-center text-2xl text-text-main">
                        <UserIcon size={32} />
                      </div>
                      <div>
                        <h2 className="text-xl font-light text-text-main">{activeSelectedUser.displayName || 'Anonymous User'}</h2>
                        <p className="text-sm text-text-secondary">{activeSelectedUser.email}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-[0.6rem] text-text-muted tracking-widest uppercase">UID: {activeSelectedUser.uid.slice(0, 8)}...</span>
                          <span className="text-[0.6rem] text-text-muted tracking-widest uppercase">Joined: {activeSelectedUser.createdAt.toDate().toLocaleDateString()}</span>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                            <input 
                              type="checkbox" 
                              checked={!!activeSelectedUser.isPaying}
                              onChange={() => handleTogglePaying(activeSelectedUser.uid, !!activeSelectedUser.isPaying)}
                              className="sr-only peer"
                            />
                            <div className="relative w-8 h-4 bg-border-secondary peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-cat-selftrust"></div>
                            <span className="text-[0.65rem] tracking-widest uppercase text-text-muted font-bold">
                              Paying Status: {activeSelectedUser.isPaying ? "Paying member" : "Unpaid / Free"}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 items-center">
                      <button
                        onClick={handleDownloadPDF}
                        disabled={isGeneratingPDF || userResults.length === 0}
                        className="flex items-center gap-2 px-5 py-3 bg-cat-selftrust text-white rounded-2xl text-xs font-bold uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {isGeneratingPDF ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            <span>PDF...</span>
                          </>
                        ) : (
                          <>
                            <Download size={14} />
                            <span>Download Report</span>
                          </>
                        )}
                      </button>

                      <div className="text-center px-6 py-3 bg-bg-input border border-border-secondary rounded-2xl">
                        <p className="text-[0.6rem] text-text-muted tracking-widest uppercase mb-1">Total Tests</p>
                        <p className="text-2xl font-extralight text-text-main">{userResults.length}</p>
                      </div>
                    </div>
                  </div>

                  {resultsLoading ? (
                    <div className="flex items-center justify-center py-20 text-text-muted">
                      <Loader2 className="animate-spin mr-3" />
                      <span className="uppercase tracking-widest text-xs">Fetching Results...</span>
                    </div>
                  ) : userResults.length > 0 ? (
                    <>
                      {/* User Stats Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-[0.6rem] text-cat-responsibility font-bold tracking-widest uppercase">Latest Responsibility</p>
                            <TrendIcon trend={getTrend('responsibility')} />
                          </div>
                          <h3 className="text-3xl font-extralight text-text-main">{latestResult?.scores.responsibility.toFixed(1) || '0.0'}</h3>
                        </div>
                        <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-[0.6rem] text-cat-selftrust font-bold tracking-widest uppercase">Latest Self Trust</p>
                            <TrendIcon trend={getTrend('selftrust')} />
                          </div>
                          <h3 className="text-3xl font-extralight text-text-main">{latestResult?.scores.selftrust.toFixed(1) || '0.0'}</h3>
                        </div>
                        <div className="bg-bg-card border border-border-main rounded-2xl p-6 space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-[0.6rem] text-cat-care font-bold tracking-widest uppercase">Latest Care</p>
                            <TrendIcon trend={getTrend('care')} />
                          </div>
                          <h3 className="text-3xl font-extralight text-text-main">{latestResult?.scores.care.toFixed(1) || '0.0'}</h3>
                        </div>
                      </div>

                      {/* Charts */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                          <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold text-center">Score Progression</h3>
                          <div className="h-[250px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={[...userResults].reverse().map(res => ({
                                date: res.timestamp.toDate().toLocaleDateString(),
                                fullDate: res.timestamp.toDate().toLocaleString(),
                                responsibility: res.scores.responsibility,
                                selftrust: res.scores.selftrust,
                                care: res.scores.care,
                                overall: (res.scores.responsibility + res.scores.selftrust + res.scores.care) / 3
                              }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" vertical={false} />
                                <XAxis 
                                  dataKey="date" 
                                  stroke="var(--text-muted)" 
                                  fontSize={10} 
                                  tickLine={false} 
                                  axisLine={false}
                                />
                                <YAxis domain={[0, 5]} stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip 
                                  contentStyle={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-secondary)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-main)' }}
                                  labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
                                />
                                <Line type="monotone" dataKey="responsibility" stroke="var(--cat-responsibility)" strokeWidth={2} dot={{ r: 4 }} name="Responsibility" />
                                <Line type="monotone" dataKey="selftrust" stroke="var(--cat-selftrust)" strokeWidth={2} dot={{ r: 4 }} name="Self Trust" />
                                <Line type="monotone" dataKey="care" stroke="var(--cat-care)" strokeWidth={2} dot={{ r: 4 }} name="Care" />
                                <Line type="monotone" dataKey="overall" stroke="var(--text-main)" strokeWidth={2} dot={{ r: 4 }} name="Overall" strokeDasharray="5 5" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                          <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold text-center">Latest Profile</h3>
                          <div className="h-[250px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={latestData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" vertical={false} />
                                <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis domain={[0, 5]} stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip 
                                  cursor={{ fill: 'var(--bg-main)' }}
                                  contentStyle={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-secondary)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-main)' }}
                                />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                  {latestData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      {/* Results Table */}
                      <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                        <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold">Assessment History</h3>
                        <div className="overflow-x-auto custom-scrollbar">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="border-b border-border-secondary">
                                <th className="py-3 px-2 text-[0.6rem] tracking-[0.15em] uppercase text-text-muted font-medium">Date</th>
                                <th className="py-3 px-2 text-[0.6rem] tracking-[0.15em] uppercase text-cat-responsibility font-medium text-center">Resp.</th>
                                <th className="py-3 px-2 text-[0.6rem] tracking-[0.15em] uppercase text-cat-selftrust font-medium text-center">Trust</th>
                                <th className="py-3 px-2 text-[0.6rem] tracking-[0.15em] uppercase text-cat-care font-medium text-center">Care</th>
                                <th className="py-3 px-2 text-[0.6rem] tracking-[0.15em] uppercase text-cat-overall font-medium text-center">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userResults.map((res) => {
                                const resTotal = res.scores.responsibility + res.scores.selftrust + res.scores.care;
                                const shadowTotal = res.shadowScores ? (res.shadowScores.responsibility + res.shadowScores.selftrust + res.shadowScores.care) : null;
                                return (
                                  <tr key={res.id} className="border-b border-border-main hover:bg-bg-main/50 transition-colors">
                                    <td className="py-4 px-2 text-[0.7rem] text-text-secondary">{res.timestamp.toDate().toLocaleString()}</td>
                                    <td className="py-4 px-2 text-[0.8rem] text-cat-responsibility font-light text-center">
                                      {res.scores.responsibility.toFixed(1)}
                                      {res.shadowScores && <span className="block text-[0.55rem] opacity-40">({res.shadowScores.responsibility.toFixed(1)})</span>}
                                    </td>
                                    <td className="py-4 px-2 text-[0.8rem] text-cat-selftrust font-light text-center">
                                      {res.scores.selftrust.toFixed(1)}
                                      {res.shadowScores && <span className="block text-[0.55rem] opacity-40">({res.shadowScores.selftrust.toFixed(1)})</span>}
                                    </td>
                                    <td className="py-4 px-2 text-[0.8rem] text-cat-care font-light text-center">
                                      {res.scores.care.toFixed(1)}
                                      {res.shadowScores && <span className="block text-[0.55rem] opacity-40">({res.shadowScores.care.toFixed(1)})</span>}
                                    </td>
                                    <td className="py-4 px-2 text-[0.8rem] text-cat-overall font-medium text-center">
                                      {resTotal.toFixed(1)}
                                      {shadowTotal !== null && <span className="block text-[0.55rem] opacity-40">({shadowTotal.toFixed(1)})</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Individual Question Scores */}
                      <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold">Individual Question Scores</h3>
                            <p className="text-[0.6rem] text-text-muted mt-1 uppercase tracking-widest font-bold">
                              Showing scores for: {activeResult ? activeResult.timestamp.toDate().toLocaleString() : 'N/A'}
                            </p>
                          </div>
                          <div className="flex items-center gap-4 flex-wrap">
                            {userResults.length > 1 && (
                              <div className="flex items-center gap-3">
                                <span className="text-[0.6rem] text-text-muted uppercase tracking-widest font-bold">Select Assessment:</span>
                                <select
                                  value={activeResult?.id || ''}
                                  onChange={(e) => setSelectedResultId(e.target.value)}
                                  className="bg-bg-input border border-border-secondary text-text-main text-xs rounded-xl px-4 py-2.5 outline-none focus:border-border-focus transition-all"
                                >
                                  {userResults.map((res) => (
                                    <option key={res.id} value={res.id}>
                                      {res.timestamp.toDate().toLocaleString()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <button
                              onClick={handleDownloadPDF}
                              disabled={isGeneratingPDF || !activeResult}
                              className="flex items-center gap-2 px-4 py-2 bg-bg-card border border-border-secondary rounded-xl text-text-secondary hover:text-text-main hover:bg-bg-main transition-all uppercase tracking-widest text-[0.65rem] font-bold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              title="Download full PDF Report for this assessment"
                            >
                              {isGeneratingPDF ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Download size={12} />
                              )}
                              <span>Download PDF</span>
                            </button>
                          </div>
                        </div>

                        {questions.length === 0 ? (
                          <div className="text-center py-8 text-text-muted text-xs uppercase tracking-widest">
                            No questions found in database.
                          </div>
                        ) : (
                          <div className="space-y-8">
                            {(['responsibility', 'selftrust', 'care'] as const).map((cat) => {
                              const catQuestions = questions.filter(
                                (q) => q.category === cat || q.category === `shadow_${cat}`
                              );

                              const catColorClass =
                                cat === 'responsibility'
                                  ? 'text-cat-responsibility border-cat-responsibility/20 bg-cat-responsibility/10'
                                  : cat === 'selftrust'
                                  ? 'text-cat-selftrust border-cat-selftrust/20 bg-cat-selftrust/10'
                                  : 'text-cat-care border-cat-care/20 bg-cat-care/10';

                              const catHeaderClass =
                                cat === 'responsibility'
                                  ? 'text-cat-responsibility'
                                  : cat === 'selftrust'
                                  ? 'text-cat-selftrust'
                                  : 'text-cat-care';

                              const catLabel =
                                cat === 'responsibility'
                                  ? 'Responsibility'
                                  : cat === 'selftrust'
                                  ? 'Self Trust'
                                  : 'Care';

                              if (catQuestions.length === 0) return null;

                              return (
                                <div key={cat} className="space-y-4">
                                  <h4 className={cn("text-[0.68rem] tracking-[0.25em] uppercase font-bold border-b border-border-main pb-2", catHeaderClass)}>
                                    {catLabel}
                                  </h4>
                                  <div className="space-y-3">
                                    {catQuestions.map((q) => {
                                      const answer = activeResult?.answers?.find((a) => a.questionId === q.id);
                                      const score = answer?.score;

                                      const resultIndex = userResults.findIndex((r) => r.id === activeResult?.id);
                                      const previousResult = userResults[resultIndex + 1];
                                      const prevAnswer = previousResult?.answers?.find((a) => a.questionId === q.id);
                                      const prevScore = prevAnswer?.score || 0;
                                      const trend = prevScore > 0 && typeof score === 'number' ? score - prevScore : 0;

                                      return (
                                        <div
                                          key={q.id}
                                          className="flex items-start justify-between gap-6 p-4 rounded-xl bg-bg-input border border-border-secondary hover:border-border-main transition-colors"
                                        >
                                          <div className="flex-1 min-w-0 space-y-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {q.isShadow && (
                                                <span className="text-[0.55rem] tracking-wider uppercase bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-full font-bold">
                                                  Shadow
                                                </span>
                                              )}
                                              <span className="text-[0.6rem] text-text-muted tracking-widest uppercase">
                                                Order: {q.order}
                                              </span>
                                            </div>
                                            <Markdown
                                              className="markdown-body text-sm text-text-main leading-relaxed"
                                              content={q.text}
                                            />
                                          </div>
                                          <div className="flex items-center gap-4 shrink-0 mt-1">
                                            {previousResult && trend !== 0 && (
                                              <div
                                                className="flex items-center gap-1"
                                                title={trend > 0 ? `Increased by ${trend}` : `Decreased by ${Math.abs(trend)}`}
                                              >
                                                {trend > 0 ? (
                                                  <TrendingUp size={16} className="text-emerald-500" />
                                                ) : (
                                                  <TrendingDown size={16} className="text-red-500" />
                                                )}
                                                <span className={cn("text-[0.65rem] font-bold", trend > 0 ? "text-emerald-500" : "text-red-500")}>
                                                  {trend > 0 ? `+${trend}` : trend}
                                                </span>
                                              </div>
                                            )}
                                            <div className="flex flex-col items-center gap-0.5 min-w-[40px]">
                                              <span className="text-[0.5rem] uppercase text-text-muted tracking-widest">Score</span>
                                              <span
                                                className={cn(
                                                  "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border",
                                                  score !== undefined ? catColorClass : "bg-bg-card border-border-secondary text-text-muted"
                                                )}
                                              >
                                                {score !== undefined ? score : '-'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="bg-bg-card border border-border-main rounded-3xl p-20 text-center space-y-4">
                      <div className="w-16 h-16 bg-bg-main rounded-full flex items-center justify-center mx-auto text-text-muted">
                        <ClipboardList size={32} />
                      </div>
                      <p className="text-text-muted uppercase tracking-[0.2em] text-xs">No assessment results found for this user.</p>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div 
                  key="platform-overview"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-8"
                >
                  <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-bg-main rounded-xl border border-border-secondary flex items-center justify-center text-cat-selftrust">
                        <TrendingUp size={24} />
                      </div>
                      <div>
                        <h2 className="text-lg font-light text-text-main">Platform Overview</h2>
                        <p className="text-[0.6rem] text-text-muted tracking-widest uppercase">Aggregate Performance Across All Users</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                      <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold text-center">Platform Average Profile</h3>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={systemAvgData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" vertical={false} />
                            <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis domain={[0, 5]} stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                            <Tooltip 
                              cursor={{ fill: 'var(--bg-main)' }}
                              contentStyle={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-secondary)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-main)' }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {systemAvgData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-bg-card border border-border-main rounded-3xl p-8 space-y-6">
                      <h3 className="text-[0.7rem] tracking-[0.2em] uppercase text-text-muted font-bold text-center">User Growth (Signups)</h3>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={users.slice().reverse().map(u => ({ date: u.createdAt.toDate().toLocaleDateString(), count: 1 })).reduce((acc: any[], curr) => {
                            const existing = acc.find(a => a.date === curr.date);
                            if (existing) existing.count += 1;
                            else acc.push(curr);
                            return acc;
                          }, [])}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" vertical={false} />
                            <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis stroke="var(--text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-secondary)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-main)' }} />
                            <Area type="monotone" dataKey="count" stroke="var(--cat-selftrust)" fill="var(--cat-selftrust)" fillOpacity={0.1} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <div className="bg-bg-card border border-border-main border-dashed rounded-3xl p-12 text-center space-y-4">
                    <div className="w-16 h-16 bg-bg-main rounded-full flex items-center justify-center mx-auto text-text-muted">
                      <Users size={32} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-text-muted text-sm uppercase tracking-widest">Select a user to drill down</p>
                      <p className="text-text-muted/60 text-xs">Use the search panel on the left to view individual performance trends and history.</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Hidden Report for PDF Generation */}
      {activeSelectedUser && activeResult && (
        <div id="metrics-report-container" data-theme="light" style={{ display: 'none' }}>
          <Report 
            user={{ displayName: activeSelectedUser.displayName || 'Guest User', email: activeSelectedUser.email }}
            results={resultsToPass}
            feedback={feedback}
            questions={questions}
            scores={scoresToPass}
            categoryDescriptions={(appConfig?.categoryDescriptions || { 
              responsibility: '', selftrust: '', care: '', overall: '',
              shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
            }) as Record<Category, string>}
            acknowledgmentText={appConfig?.acknowledgmentText}
            howToReadText={appConfig?.howToReadText}
            scoreInsight={appConfig?.scoreInsight}
            categoryShortDescriptions={(appConfig?.categoryShortDescriptions || { 
              responsibility: '', selftrust: '', care: '', overall: '',
              shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
            }) as Record<Category, string>}
            showLogo={appConfig?.showLogo !== false}
            pdfExcludeHistory={appConfig?.pdfExcludeHistory}
          />
        </div>
      )}
    </div>
  );
};
